/**
 * Client API typé (léger) — un point d'entrée unique vers le backend FastAPI.
 * Ajoute automatiquement le jeton Bearer et normalise les erreurs {code,message,details}.
 */
import axios from 'axios';

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const TOKEN_KEY = 'vd_token';

const client = axios.create({ baseURL: BASE });

/** Jeton courant : priorité à sessionStorage (onglet d'impersonation isolé) puis localStorage. */
export const getToken = () => sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
export const setSessionToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
export const clearTokens = () => { sessionStorage.removeItem(TOKEN_KEY); localStorage.removeItem(TOKEN_KEY); };

// URL d'un fichier stocké (image d'étiquette, logo…) pour <img src>.
// Le fichier est cloisonné par établissement : on transmet le jeton via ?auth= (les <img>
// ne peuvent pas envoyer d'en-tête Authorization).
export const fileUrl = (id) => {
  if (!id) return null;
  const token = getToken();
  return `${BASE}/files/${id}${token ? `?auth=${encodeURIComponent(token)}` : ''}`;
};

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Indicateur d'enregistrement automatique : signalé pour les éditions (PATCH/PUT).
  if (['patch', 'put'].includes((config.method || '').toLowerCase())) {
    window.dispatchEvent(new CustomEvent('vd-save', { detail: { state: 'saving' } }));
  }
  return config;
});

function signalSaved(config, ok, code) {
  if (config && ['patch', 'put'].includes((config.method || '').toLowerCase())) {
    let state = 'saved';
    if (!ok) state = code === 409 ? 'conflict' : (code ? 'error' : 'offline');
    window.dispatchEvent(new CustomEvent('vd-save', { detail: { state } }));
  }
}

client.interceptors.response.use(
  (res) => { signalSaved(res.config, true); return res; },
  (err) => {
    const status = err.response?.status;
    const url = String(err.config?.url || '');
    // 401 : session révoquée/expirée → déconnexion et retour au login (hors tentative de login).
    if (status === 401 && !url.includes('/auth/login')) {
      sessionStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(TOKEN_KEY);
      window.dispatchEvent(new CustomEvent('vd-logout'));
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    signalSaved(err.config, false, status);
    const detail = err.response?.data?.message || err.response?.data?.detail;
    const message = typeof detail === 'string' ? detail : err.message || 'Erreur inattendue';
    return Promise.reject({ code: err.response?.status, message, response: err.response });
  }
);

// --- Authentification ---
export const authApi = {
  login: (email, password) => client.post('/auth/login', { email, password }).then((r) => r.data),
  requestReset: (email, baseUrl) => client.post('/auth/request-reset', { email, base_url: baseUrl }).then((r) => r.data),
  resetPassword: (token, password) => client.post('/auth/reset-password', { token, password }).then((r) => r.data),
  me: () => client.get('/auth/me').then((r) => r.data),
  updateProfile: (payload) => client.patch('/auth/profile', payload).then((r) => r.data),
};

// --- Super-admin (plateforme) v0.2 ---
function triggerDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

export const adminApi = {
  dashboard: () => client.get('/admin/dashboard').then((r) => r.data),
  meta: () => client.get('/admin/meta').then((r) => r.data),
  instance: () => client.get('/admin/instance').then((r) => r.data),
  // Établissements
  tenants: () => client.get('/admin/tenants').then((r) => r.data),
  createTenant: (p) => client.post('/admin/tenants', p).then((r) => r.data),
  updateTenant: (id, p) => client.patch(`/admin/tenants/${id}`, p).then((r) => r.data),
  deleteTenant: (id) => client.delete(`/admin/tenants/${id}`).then((r) => r.data),
  disableTenant: (id) => client.post(`/admin/tenants/${id}/disable`).then((r) => r.data),
  enableTenant: (id) => client.post(`/admin/tenants/${id}/enable`).then((r) => r.data),
  exportTenant: async (id, filename) => {
    const r = await client.get(`/admin/tenants/${id}/export`, { responseType: 'blob' });
    triggerDownload(r.data, filename);
  },
  // Utilisateurs
  users: () => client.get('/admin/users').then((r) => r.data),
  createUser: (p) => client.post('/admin/users', p).then((r) => r.data),
  updateUser: (id, p) => client.patch(`/admin/users/${id}`, p).then((r) => r.data),
  deleteUser: (id) => client.delete(`/admin/users/${id}`).then((r) => r.data),
  blockUser: (id) => client.post(`/admin/users/${id}/block`).then((r) => r.data),
  unblockUser: (id) => client.post(`/admin/users/${id}/unblock`).then((r) => r.data),
  resetPassword: (id) => client.post(`/admin/users/${id}/reset-password`).then((r) => r.data),
  setPassword: (id, p) => client.post(`/admin/users/${id}/set-password`, p).then((r) => r.data),
  sendAccess: (id, p) => client.post(`/admin/users/${id}/send-access`, p).then((r) => r.data),
  impersonate: (id, p) => client.post(`/admin/users/${id}/impersonate`, p || {}).then((r) => r.data),
  // Journal
  logs: (params) => client.get('/admin/logs', { params }).then((r) => r.data),
  purgeLogs: () => client.delete('/admin/logs').then((r) => r.data),
  purgeOlder: (days) => client.post('/admin/logs/purge-older', { days }).then((r) => r.data),
  exportLogsCsv: async (params, filename) => {
    const r = await client.get('/admin/logs/export.csv', { params, responseType: 'blob' });
    triggerDownload(r.data, filename);
  },
  // Paramètres
  settings: () => client.get('/admin/settings').then((r) => r.data),
  putSettings: (section, p) => client.put(`/admin/settings/${section}`, p).then((r) => r.data),
  getSmtp: () => client.get('/admin/settings/smtp').then((r) => r.data),
  putSmtp: (p) => client.put('/admin/settings/smtp', p).then((r) => r.data),
  testSmtp: (p) => client.post('/admin/settings/smtp/test', p || {}).then((r) => r.data),
  runNow: () => client.post('/admin/scheduler/run-now').then((r) => r.data),
  getAccount: () => client.get('/admin/account').then((r) => r.data),
  updateAccount: (p) => client.put('/admin/account', p).then((r) => r.data),
  aiConfig: () => client.get('/admin/ai').then((r) => r.data),
  updateAiConfig: (p) => client.put('/admin/ai', p).then((r) => r.data),
  saveAiKey: (provider, key) => client.post('/admin/ai/key', { provider, key }).then((r) => r.data),
  deleteAiKey: (provider) => client.delete(`/admin/ai/key/${provider}`).then((r) => r.data),
  // Export / import
  exportFile: async (kind, filename) => {
    const r = await client.get(`/admin/export/${kind}`, { responseType: 'blob' });
    triggerDownload(r.data, filename);
  },
  importConfig: (data, confirm) => client.post('/admin/import/config', { data, confirm }).then((r) => r.data),
};

export const impersonateExchange = (token) =>
  client.post('/auth/impersonate/exchange', { token }).then((r) => r.data);

// --- Tableau de bord ---
export const dashboardApi = {
  stats: () => client.get('/dashboard/stats').then((r) => r.data),
  alerts: () => client.get('/dashboard/alerts').then((r) => r.data),
  movements: () => client.get('/dashboard/movements').then((r) => r.data),
};

// --- La cave ---
export const caveApi = {
  stock: (params) => client.get('/cave/stock', { params }).then((r) => r.data),
  cuvee: (id) => client.get(`/cuvees/${id}`).then((r) => r.data),
  movement: (refId, body) => client.post(`/references/${refId}/movement`, body).then((r) => r.data),
  undo: (movementId) => client.post(`/movements/${movementId}/undo`).then((r) => r.data),
  setHearts: (cuveeId, hearts) => client.patch(`/cuvees/${cuveeId}/hearts`, { hearts }).then((r) => r.data),
  setTags: (cuveeId, tags) => client.patch(`/cuvees/${cuveeId}/tags`, { tags }).then((r) => r.data),
  mergeTags: (source, target) => client.post('/tags/merge', { source, target }).then((r) => r.data),
  updateTag: (name, payload) => client.patch(`/tags/${encodeURIComponent(name)}`, payload).then((r) => r.data),
  deleteTag: (name, reassign = '') => client.delete(`/tags/${encodeURIComponent(name)}`, { params: { reassign } }).then((r) => r.data),
  tags: () => client.get('/tags').then((r) => r.data),
  cepages: () => client.get('/meta/cepages').then((r) => r.data),
  updateCuvee: (id, payload) => client.patch(`/cuvees/${id}`, payload).then((r) => r.data),
  createCuvee: (payload = {}) => client.post('/cuvees', payload).then((r) => r.data),
  completeCuveeAI: (id) => client.post(`/cuvees/${id}/complete-ai`).then((r) => r.data),
  setCuveeDomain: (id, payload) => client.post(`/cuvees/${id}/domain`, payload).then((r) => r.data),
  refEntries: (refId) => client.get(`/cave/references/${refId}/entries`).then((r) => r.data),
  setRefQuantity: (refId, quantity) => client.patch(`/cave/references/${refId}/quantity`, { quantity }).then((r) => r.data),
  updateReference: (refId, patch) => client.patch(`/cave/references/${refId}`, patch).then((r) => r.data),
  recomputeRefPrices: (refId) => client.post(`/cave/references/${refId}/recompute-prices`).then((r) => r.data),
  bulkRefFlags: (payload) => client.post('/cave/references/bulk-flags', payload).then((r) => r.data),
  bulkRecompute: (referenceIds) => client.post('/cave/references/bulk-recompute', { reference_ids: referenceIds }).then((r) => r.data),
  archiveCuvee: (id, archived) => client.patch(`/cuvees/${id}/archive`, { archived }).then((r) => r.data),
  deleteCuvee: (id) => client.delete(`/cuvees/${id}`).then((r) => r.data),
  archives: () => client.get('/archives').then((r) => r.data),
  updateVintage: (id, payload) => client.patch(`/vintages/${id}`, payload).then((r) => r.data),
  uploadImage: (cuveeId, kind, file) => {
    const fd = new FormData(); fd.append('kind', kind); fd.append('file', file);
    return client.post(`/cuvees/${cuveeId}/image`, fd).then((r) => r.data);
  },
  searchImages: (cuveeId) => client.post(`/cuvees/${cuveeId}/search-images`).then((r) => r.data),
  imageFromUrl: (cuveeId, kind, url) => client.post(`/cuvees/${cuveeId}/image-from-url`, { kind, url }).then((r) => r.data),
  clearImage: (cuveeId, kind) => client.delete(`/cuvees/${cuveeId}/image`, { params: { kind } }).then((r) => r.data),
  rotateImage: (fileId, degrees = 90) => client.post(`/files/${fileId}/rotate`, { degrees }).then((r) => r.data),
  setFlags: (cuveeId, body) => client.patch(`/cuvees/${cuveeId}/flags`, body).then((r) => r.data),
  degustes: (params) => client.get('/cave/degustes', { params: params || {} }).then((r) => r.data),
  createDeguste: (payload) => client.post('/cave/degustes', payload).then((r) => r.data),
  degusteCommander: (id) => client.post(`/cave/degustes/${id}/commander`).then((r) => r.data),
  degusteDecouvrir: (id) => client.post(`/cave/degustes/${id}/decouvrir`).then((r) => r.data),
  degusteToCave: (id, payload) => client.post(`/cave/degustes/${id}/to-cave`, payload || {}).then((r) => r.data),
  deleteDeguste: (id) => client.delete(`/cave/degustes/${id}`).then((r) => r.data),
  addVoiceNote: (id, blob, filename) => { const fd = new FormData(); fd.append('file', blob, filename); return client.post(`/cave/degustes/${id}/voice`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data); },
  deleteVoiceNote: (id, noteId) => client.delete(`/cave/degustes/${id}/voice/${noteId}`).then((r) => r.data),
  movements: (params) => client.get('/movements', { params: params || {} }).then((r) => r.data),
  refMovements: (refId) => client.get(`/references/${refId}/movements`).then((r) => r.data),
  locationsData: () => client.get('/cave/locations').then((r) => r.data),
  placeLocations: (refId, body) => client.post(`/cave/references/${refId}/locations`, body).then((r) => r.data),
  undoLocations: (refId, body) => client.post(`/cave/references/${refId}/locations/undo`, body).then((r) => r.data),
};

// --- Vignobles ---
export const vignoblesApi = {
  domains: (status) => client.get('/domains', { params: status ? { status } : {} }).then((r) => r.data),
  domain: (id) => client.get(`/domains/${id}`).then((r) => r.data),
  update: (id, payload) => client.patch(`/domains/${id}`, payload).then((r) => r.data),
  archiveDomain: (id, archived) => client.patch(`/domains/${id}/archive`, { archived }).then((r) => r.data),
  deleteDomain: (id) => client.delete(`/domains/${id}`).then((r) => r.data),
  setPosition: (id, lat, lng) => client.patch(`/domains/${id}/position`, { lat, lng }).then((r) => r.data),
  create: (payload) => client.post('/domains', payload).then((r) => r.data),
  enrich: (query) => client.post('/domains/enrich', { query }).then((r) => r.data),
  uploadImage: (id, kind, file) => {
    const fd = new FormData(); fd.append('kind', kind); fd.append('file', file);
    return client.post(`/domains/${id}/image`, fd).then((r) => r.data);
  },
  searchImages: (id) => client.post(`/domains/${id}/search-images`).then((r) => r.data),
  imageFromUrl: (id, kind, url) => client.post(`/domains/${id}/image-from-url`, { kind, url }).then((r) => r.data),
  clearImage: (id, kind) => client.delete(`/domains/${id}/image`, { params: { kind } }).then((r) => r.data),
  recommendations: (params) => client.get('/recommendations', { params: params || {} }).then((r) => r.data),
  extractReco: (input) => client.post('/recommendations/extract', { input }).then((r) => r.data),
  extractRecoPhoto: (image_base64) => client.post('/recommendations/extract-photo', { image_base64 }).then((r) => r.data),
  createReco: (payload) => client.post('/recommendations', payload).then((r) => r.data),
  updateReco: (id, payload) => client.patch(`/recommendations/${id}`, payload).then((r) => r.data),
  discovered: (id, date, note) => client.post(`/recommendations/${id}/discovered`, { date, note }).then((r) => r.data),
  undiscovered: (id) => client.post(`/recommendations/${id}/undiscovered`).then((r) => r.data),
  deleteReco: (id) => client.delete(`/recommendations/${id}`).then((r) => r.data),
  recoToDomain: (id) => client.post(`/recommendations/${id}/create-domain`).then((r) => r.data),
  recoTastings: (id) => client.get(`/recommendations/${id}/tastings`).then((r) => r.data),
  appellations: (q) => client.get('/appellations', { params: q ? { q } : {} }).then((r) => r.data),
  appellationPays: () => client.get('/appellations/pays').then((r) => r.data),
  appellationCepages: (pays) => client.get('/appellations/cepages', { params: pays ? { pays } : {} }).then((r) => r.data),
  caveAppellations: () => client.get('/cave-appellations').then((r) => r.data),
  enrichAppellation: (name) => client.post('/appellations/enrich', { name }).then((r) => r.data),
  createAppellation: (payload) => client.post('/appellations', payload).then((r) => r.data),
  importAppellations: (content, format, commit, defaultPays) => client.post('/appellations/import', { content, format, commit, default_pays: defaultPays }).then((r) => r.data),
  exportAppellations: async (format) => {
    const res = await client.get('/appellations/export', { params: { format }, responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `appellations.${format === 'csv' ? 'csv' : 'json'}`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// --- Paramètres, recherche, IA ---
export const metaApi = {
  settings: () => client.get('/settings').then((r) => r.data),
  updateSettings: (body) => client.put('/settings', body).then((r) => r.data),
  constants: () => client.get('/constants').then((r) => r.data),
  search: (q) => client.get('/search', { params: { q } }).then((r) => r.data),
  locations: () => client.get('/locations').then((r) => r.data),
};

// --- Scanner & entrées ---
export const scannerApi = {
  detect: (image_base64) => client.post('/scanner/detect', { image_base64 }).then((r) => r.data),
  transcribe: (blob, filename) => { const fd = new FormData(); fd.append('file', blob, filename); return client.post('/scanner/transcribe', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data); },
  analyzeLabel: (images, enrich = true) => client.post('/scanner/analyze-label', { images, enrich }).then((r) => r.data),
  orderLabels: (images) => client.post('/scanner/order-labels', { images }).then((r) => r.data),
  analyzeInvoice: (image_base64) => client.post('/scanner/analyze-invoice', { image_base64 }).then((r) => r.data),
  suggestLocation: (format, count) => client.get('/scanner/suggest-location', { params: { format, count } }).then((r) => r.data),
  entry: (payload) => client.post('/scanner/entry', payload).then((r) => r.data),
  batch: (items) => client.post('/scanner/batch', { items }).then((r) => r.data),
  invoice: (payload) => client.post('/scanner/invoice', payload).then((r) => r.data),
  undo: (groupId) => client.post(`/scanner/undo/${groupId}`).then((r) => r.data),
  searchRefs: (q) => client.get('/references/search', { params: { q } }).then((r) => r.data),
};

// --- Sortie (mode service) ---
export const sortieApi = {
  search: (q) => client.get('/sortie/search', { params: { q } }).then((r) => r.data),
  exit: (payload) => client.post('/sortie/exit', payload).then((r) => r.data),
  undo: (movementId) => client.post(`/sortie/undo/${movementId}`).then((r) => r.data),
  recent: () => client.get('/sortie/recent').then((r) => r.data),
  openBottles: () => client.get('/sortie/open-bottles').then((r) => r.data),
};

// --- Alertes & commandes ---
export const alertsApi = {
  ignore: (payload) => client.post('/alerts/ignore', payload).then((r) => r.data),
  orderDraft: (referenceId, coverage = 3) => client.get(`/alerts/order-draft/${referenceId}`, { params: { coverage } }).then((r) => r.data),
  order: (payload) => client.post('/alerts/order', payload).then((r) => r.data),
};

// --- Inventaire guidé ---
export const inventoryApi = {
  zones: () => client.get('/inventory/zones').then((r) => r.data),
  references: (zone) => client.get('/inventory/references', { params: { zone } }).then((r) => r.data),
  validate: (payload) => client.post('/inventory/validate', payload).then((r) => r.data),
};

// --- Piloter (statistiques, marges, valorisation) ---
export const piloterApi = {
  overview: (params) => client.get('/piloter/overview', { params: params || {} }).then((r) => r.data),
  valorisation: (params) => client.get('/piloter/valorisation', { params: params || {} }).then((r) => r.data),
  // Téléchargement authentifié (blob) des exports comptables, puis déclenchement du download.
  download: async (kind, date) => {
    const ext = kind === 'pdf' ? 'pdf' : 'xlsx';
    const res = await client.get(`/piloter/valorisation/export.${ext}`, {
      params: date ? { date } : {}, responseType: 'blob',
    });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `valorisation_${date || 'stock'}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
  monthlyReport: (year) => client.get('/piloter/report/monthly', { params: year ? { year } : {} }).then((r) => r.data),
  downloadMonthlyReport: async (year) => {
    const res = await client.get('/piloter/report/monthly/export.pdf', { params: year ? { year } : {}, responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `etat_mensuel_${year}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// --- Carte des vins (Module 5) ---
export const carteApi = {
  constants: () => client.get('/carte/constants').then((r) => r.data),
  settings: () => client.get('/carte/settings').then((r) => r.data),
  saveSettings: (payload) => client.post('/carte/settings', payload).then((r) => r.data),
  deleteSettings: (id) => client.delete(`/carte/settings/${id}`).then((r) => r.data),
  preview: (payload, lang = 'fr') => client.post('/carte/preview', payload, { params: { lang } }).then((r) => r.data),
  generate: async (settingsId, lang, name) => {
    const res = await client.get('/carte/generate', { params: { settings_id: settingsId, lang }, responseType: 'blob' });
    const d = new Date().toISOString().slice(0, 10);
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `${d} - ${name || 'Carte'} - ${(lang || 'fr').toUpperCase()}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  // Traductions (par modèle × langue, sans relecture)
  translateTemplate: (templateId, lang, options) => client.post('/carte/translate', { template_id: templateId, lang, options }).then((r) => r.data),
  translationPreview: (templateId, lang) => client.get(`/carte/translations/${templateId}/${lang}`).then((r) => r.data),
  // Upload page/logo/bandeau
  upload: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/carte/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data);
  },
  // Éditions & publication
  editions: () => client.get('/carte/editions').then((r) => r.data),
  publish: (settingsId) => client.post('/carte/publish', { settings_id: settingsId }).then((r) => r.data),
  unpublish: (settingsId) => client.post('/carte/unpublish', { settings_id: settingsId }).then((r) => r.data),
  published: () => client.get('/carte/published').then((r) => r.data),
  portalDefault: () => client.get('/carte/portal-default').then((r) => r.data),
  setPortalDefault: (templateId, lang) => client.post('/carte/portal-default', { template_id: templateId, lang }).then((r) => r.data),
  setPortalTabs: (tabIds) => client.post('/carte/portal-tabs', { tab_ids: tabIds }).then((r) => r.data),
  qrPngUrl: (slug, m, lang) => {
    const p = new URLSearchParams({ base_url: window.location.origin, slug });
    if (m) p.set('m', m);
    if (lang) p.set('lang', lang);
    return `${BASE}/carte/qr.png?${p.toString()}`;
  },
  downloadQr: async (kind, slug, m, lang) => {
    const base = window.location.origin;
    const path = kind.startsWith('plate') ? 'qr-plate.pdf' : `qr.${kind}`;
    const params = { base_url: base, slug };
    if (m) params.m = m;
    if (lang) params.lang = lang;
    if (kind.startsWith('plate')) params.target = kind.split('-')[1];
    const res = await client.get(`/carte/${path}`, { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `qr_${slug}${lang ? '_' + lang : ''}.${kind.startsWith('plate') ? 'pdf' : kind}`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  // Carte en ligne publique (sans authentification)
  portalLanguages: (slug, m) => client.get(`/public/carte/${slug}/languages`, { params: m ? { m } : {} }).then((r) => r.data),
  portalStructure: (slug) => client.get(`/public/carte/${slug}/portal`).then((r) => r.data),
  publicCarte: (slug, lang, m) => client.get(`/public/carte/${slug}`, { params: { ...(lang ? { lang } : {}), ...(m ? { m } : {}) } }).then((r) => r.data),
};

// --- Paramètres (Module 7) ---
export const parametresApi = {
  meta: () => client.get('/parametres/meta').then((r) => r.data),
  updateEstablishment: (payload) => client.patch('/parametres/establishment', payload).then((r) => r.data),
  users: () => client.get('/parametres/users').then((r) => r.data),
  inviteUser: (payload) => client.post('/parametres/users', payload).then((r) => r.data),
  appInstance: () => client.get('/parametres/instance').then((r) => r.data),  updateUser: (id, payload) => client.patch(`/parametres/users/${id}`, payload).then((r) => r.data),
  deleteUser: (id) => client.delete(`/parametres/users/${id}`).then((r) => r.data),
  resendCredentials: (id) => client.post(`/parametres/users/${id}/resend-credentials`).then((r) => r.data),
  spaces: () => client.get('/parametres/spaces').then((r) => r.data),
  saveSpace: (payload) => client.post('/parametres/spaces', payload).then((r) => r.data),
  deleteSpace: (id) => client.delete(`/parametres/spaces/${id}`).then((r) => r.data),
  suggestSpace: (image_base64) => client.post('/parametres/spaces/suggest', { image_base64 }).then((r) => r.data),
  saleModes: () => client.get('/parametres/sale-modes').then((r) => r.data),
  saveSaleModes: (sale_modes) => client.put('/parametres/sale-modes', { sale_modes }).then((r) => r.data),
  recompute: () => client.post('/parametres/sale-modes/recompute').then((r) => r.data),
  suppliers: () => client.get('/parametres/suppliers').then((r) => r.data),
  saveAiKey: (provider, api_key) => client.put('/parametres/ai-keys', { provider, api_key }).then((r) => r.data),
  deleteAiKey: (provider) => client.delete(`/parametres/ai-keys/${provider}`).then((r) => r.data),
  savePlacesKey: (api_key) => client.put('/parametres/google-places-key', { api_key }).then((r) => r.data),
  deletePlacesKey: () => client.delete('/parametres/google-places-key').then((r) => r.data),
  integrationsStatus: () => client.get('/parametres/integrations-status').then((r) => r.data),
  specificities: () => client.get('/parametres/specificities').then((r) => r.data),
  addSpecificity: (label) => client.post('/parametres/specificities', { label }).then((r) => r.data),
  renameSpecificity: (key, label) => client.patch(`/parametres/specificities/${key}`, { label }).then((r) => r.data),
  deleteSpecificity: (key) => client.delete(`/parametres/specificities/${key}`).then((r) => r.data),
  saveSupplier: (payload) => client.post('/parametres/suppliers', payload).then((r) => r.data),
  deleteSupplier: (id) => client.delete(`/parametres/suppliers/${id}`).then((r) => r.data),
  importAnalyze: (file) => { const fd = new FormData(); fd.append('file', file); return client.post('/parametres/import/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data); },
  importCommit: (rows) => client.post('/parametres/import/commit', { rows }).then((r) => r.data),
  importUndo: (batch_id) => client.post('/parametres/import/undo', { batch_id }).then((r) => r.data),
  csvAnalyze: (file) => { const fd = new FormData(); fd.append('file', file); return client.post('/parametres/import/csv/analyze', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data); },
  csvCommit: (file, mapping, on_exists, qty_mode) => { const fd = new FormData(); fd.append('file', file); fd.append('mapping', JSON.stringify(mapping)); fd.append('on_exists', on_exists); fd.append('qty_mode', qty_mode); return client.post('/parametres/import/csv/commit', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data); },
  spaceLabelsUrl: (id) => `${BASE}/parametres/spaces/${id}/labels.pdf?base_url=${window.location.origin}`,
  download: async (kind, fmt, isPublic) => {
    const res = await client.get(`/parametres/export/${kind}`, { params: { fmt, public: isPublic }, responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = `${kind}.${fmt === 'csv' ? 'csv' : kind === 'sauvegarde' ? 'json' : 'xlsx'}`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
  downloadLabels: async (id) => {
    const res = await client.get(`/parametres/spaces/${id}/labels.pdf`, { params: { base_url: window.location.origin }, responseType: 'blob' });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = 'etiquettes.pdf';
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// --- Synchronisation hors-ligne ---
export const syncApi = {
  snapshot: () => client.get('/sync/snapshot').then((r) => r.data),
};

// --- Achats (fournisseurs, commandes, documents, rapprochement) ---
export const achatsApi = {
  summary: () => client.get('/achats/summary').then((r) => r.data),
  aTraiter: () => client.get('/achats/a-traiter').then((r) => r.data),
  documents: (status) => client.get('/achats/documents', { params: status ? { status } : {} }).then((r) => r.data),
  document: (id) => client.get(`/achats/documents/${id}`).then((r) => r.data),
  uploadDocument: (file) => { const fd = new FormData(); fd.append('file', file); return client.post('/achats/documents/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data); },
  reconcile: (id, orderId) => client.post(`/achats/documents/${id}/reconcile`, { order_id: orderId }).then((r) => r.data),
  validateDocument: (id, lines, updatePrices = true) => client.post(`/achats/documents/${id}/validate`, { lines, update_prices: updatePrices }).then((r) => r.data),
  orders: (status) => client.get('/achats/orders', { params: status ? { status } : {} }).then((r) => r.data),
  saveOrder: (payload) => client.post('/achats/orders', payload).then((r) => r.data),
  deleteOrder: (id) => client.delete(`/achats/orders/${id}`).then((r) => r.data),
  updateDocument: (id, payload) => client.patch(`/achats/documents/${id}`, payload).then((r) => r.data),
  deleteDocument: (id) => client.delete(`/achats/documents/${id}`).then((r) => r.data),
  suppliers: () => client.get('/achats/suppliers').then((r) => r.data),
  saveSupplier: (payload) => client.post('/achats/suppliers', payload).then((r) => r.data),
  deleteSupplier: (id) => client.delete(`/achats/suppliers/${id}`).then((r) => r.data),
  wishes: () => client.get('/achats/wishes').then((r) => r.data),
  saveWish: (payload) => client.post('/achats/wishes', payload).then((r) => r.data),
  updateWish: (id, payload) => client.patch(`/achats/wishes/${id}`, payload).then((r) => r.data),
  deleteWish: (id) => client.delete(`/achats/wishes/${id}`).then((r) => r.data),
  wishesToOrder: (wishIds) => client.post('/achats/wishes/to-order', { wish_ids: wishIds }).then((r) => r.data),
};

export default client;
